import { type FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { errorMessage } from '@/shared/api/errors';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { AlertIcon } from '@/shared/ui/icons';
import { Logo } from '@/shared/ui/Logo';
import { PasswordInput } from '@/shared/ui/PasswordInput';

import { RecoveryCodesPanel, SetupPanel, VerifyPanel } from './SecondFactor';
import { isSecondFactorPending, type SecondFactorStep } from './types';
import { useLogin } from './useSession';

interface RedirectState {
  from?: string;
  /** Why the session ended, when someone said (`RequireAuth`, contract v10.45): the API
   * (`second_factor_required`) or this front (`two_factor_reset`). */
  reason?: string;
}

/** The line the door says for a session the API ended with a reason. */
const ENDED: Record<string, string> = {
  second_factor_required:
    'Para seguir, activa el doble factor: escribe tu contraseña y te explicamos cómo.',
  /** Not the API's: Administración → Usuarios, after resetting one's own second factor. */
  two_factor_reset: 'Has reiniciado tu doble factor y se ha cerrado la sesión. Vuelve a entrar.',
};

/**
 * Where the door is. The password first; then, since contract v10.45 (ADR 0039 of the
 * back), the code of the app (`verify`) or setting the app up (`setup`), and after a setup
 * the ten recovery codes, shown once, before going in.
 */
type Step = { kind: 'password' } | { kind: SecondFactorStep } | { kind: 'codes'; codes: string[] };

const HEADINGS: Record<Step['kind'], { title: string; lead: string }> = {
  password: {
    title: 'Entrar',
    lead: 'Con el correo y la contraseña que te dio quien administra la aplicación.',
  },
  verify: {
    title: 'Escribe el código',
    lead: 'Abre en el móvil la aplicación autenticadora y escribe las seis cifras que salen para esta cuenta.',
  },
  setup: {
    title: 'Activa el doble factor',
    lead: 'Para entrar hace falta, además de la contraseña, un código que da una aplicación del móvil. Se prepara una vez.',
  },
  codes: {
    title: 'Guarda tus códigos de recuperación',
    lead: 'El doble factor está activado. Con estos códigos entras si un día no tienes el móvil.',
  },
};

/**
 * The door. On a desktop, half of it is the form and the other half is the logo on the
 * dark wall of the shop, and nothing else: whoever gets here already knows what this is.
 * On a phone it is the logo and the form.
 *
 * Nobody is named: the person who hands out passwords is «quien administra la
 * aplicación», whoever that is the day this is read. And there is no «he olvidado mi
 * contraseña» on purpose: the password comes from that person, in hand or as an
 * invitation link they issue from the panel (`/invitacion/:token`), never from a
 * self-service flow on the door.
 *
 * A right password is not always a session (contract v10.45): with `second_factor` in the
 * answer the API opened nothing yet, `/api/auth/me` still says 401, and the door stays on
 * screen with the next step. Ten minutes later the step is gone and the person is back at
 * the password with a sentence saying why.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const state = location.state as RedirectState | null;
  const [step, setStep] = useState<Step>({ kind: 'password' });
  /** Why the person is at the password: the API ended the session for a reason, or the
   * second step ran out of time. */
  const [notice, setNotice] = useState<string | null>(
    () => (state?.reason ? ENDED[state.reason] : undefined) ?? null,
  );

  const redirectTo = state?.from ?? '/correos';
  const heading = HEADINGS[step.kind];

  // The ten codes are on screen once and the session is already open: a reload or a closed
  // tab here would go in without them, and nothing shows them again. The browser asks first.
  useEffect(() => {
    if (step.kind !== 'codes') return undefined;
    function onBeforeUnload(event: BeforeUnloadEvent): void {
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [step.kind]);

  function enter() {
    void navigate(redirectTo, { replace: true });
  }

  function backToPassword(message: string | null) {
    login.reset();
    setPassword('');
    setNotice(message);
    setStep({ kind: 'password' });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice(null);
    login.mutate(
      { email, password },
      {
        onSuccess: (result) => {
          if (isSecondFactorPending(result)) {
            setPassword('');
            setStep({ kind: result.second_factor });
            return;
          }
          enter();
        },
      },
    );
  }

  return (
    <main className="bg-paper grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <section className="bg-nav relative hidden items-center justify-center overflow-hidden p-12 lg:flex">
        {/* A grid of nuts, faint, like the wall of the shop. Pure CSS, no image. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1.2px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
        <Logo inverted className="relative h-28 w-auto" />
      </section>

      <section className="flex items-center justify-center px-5 py-10 sm:px-6 sm:py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex justify-center lg:hidden">
            <Logo className="h-14" />
          </div>
          <h2 className="font-display text-ink text-[26px] font-semibold">{heading.title}</h2>
          <p className="text-ink-3 mt-1 text-[15px]">{heading.lead}</p>

          {step.kind === 'verify' || step.kind === 'setup' ? (
            <p className="text-ink-3 mt-2 text-[13.5px] break-words">
              Entrando como <strong className="text-ink-2">{email}</strong> ·{' '}
              <button type="button" className="link" onClick={() => backToPassword(null)}>
                Cambiar
              </button>
            </p>
          ) : null}

          {step.kind === 'password' ? (
            <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4" noValidate>
              {notice ? (
                <p
                  role="alert"
                  className="bg-warn-soft text-warn border-warn/25 flex items-start gap-2 rounded-md border px-3 py-2 text-[14px]"
                >
                  <AlertIcon size={16} className="mt-0.5 shrink-0" />
                  <span>{notice}</span>
                </p>
              ) : null}
              <Input
                label="Correo electrónico"
                type="email"
                name="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <PasswordInput
                label="Contraseña"
                name="password"
                autoComplete="current-password"
                required
                autoFocus={notice !== null && email !== ''}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />

              {login.isError ? (
                <p
                  role="alert"
                  className="bg-danger-soft text-danger border-danger/20 flex items-start gap-2 rounded-md border px-3 py-2 text-[14px]"
                >
                  <AlertIcon size={16} className="mt-0.5 shrink-0" />
                  <span>{errorMessage(login.error)}</span>
                </p>
              ) : null}

              <Button type="submit" size="lg" disabled={login.isPending} className="mt-1">
                {login.isPending ? 'Entrando…' : 'Entrar'}
              </Button>
            </form>
          ) : null}

          {step.kind === 'verify' ? (
            <div className="mt-7">
              <VerifyPanel onSignedIn={enter} onRestart={backToPassword} />
            </div>
          ) : null}

          {step.kind === 'setup' ? (
            <div className="mt-6">
              <SetupPanel
                submitLabel="Activar y entrar"
                allowRemember
                onEnabled={(result) => setStep({ kind: 'codes', codes: result.recovery_codes })}
                onRestart={backToPassword}
              />
            </div>
          ) : null}

          {step.kind === 'codes' ? (
            <div className="mt-6">
              <RecoveryCodesPanel
                codes={step.codes}
                account={email}
                doneLabel="Entrar"
                onDone={enter}
              />
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}
